import { useEffect, useState } from "react";
import { contacts } from "../../api/contacts.service.js";
import { contactLabel } from "../../components/Profiles/ListFilters/AddressBookContactPicker.js";

// Module-scoped cache and in-flight tracking, keyed by Address Book contact
// uuid, so a name resolved once labels its chip on the first paint of any later
// mount, and two callers asking for the same contact share one request.
const cache = new Map<string, string>();
const inFlight = new Map<string, Promise<string | null>>();

function fetchName(contactId: string): Promise<string | null> {
  const cached = cache.get(contactId);
  if (cached) return Promise.resolve(cached);
  const existing = inFlight.get(contactId);
  if (existing) return existing;

  const promise = contacts
    .getById(contactId)
    .then(contact => {
      // `getById` answers a 404 with `null` — a stale uuid in a pasted URL — and
      // a contact whose name fields are all blank has no label to offer either.
      // Neither is cached, so a later mount asks again.
      const name = contact ? contactLabel(contact) : "";
      if (!name) return null;
      cache.set(contactId, name);
      return name;
    })
    .catch(err => {
      // The name is a UI-only nicety for the chip; on failure the chip keeps
      // the raw uuid. No logger is wired into staff-ui yet — log so the failure
      // is at least visible in dev tools.
      console.error("[useContactName] failed to resolve contact name", contactId, err);
      return null;
    })
    .finally(() => {
      inFlight.delete(contactId);
    });
  inFlight.set(contactId, promise);
  return promise;
}

/**
 * The Address Book name of one contact, for the Associated Lawyer chip — the
 * contact counterpart of `useCompanyNames`. Single-valued, because the filter
 * holds one contact and `contacts.service` offers no batch lookup to share.
 *
 * `undefined` while there is no id, while the name is in flight, and for good
 * when the contact is gone or the lookup failed: the chip then keeps showing
 * the raw uuid. Nothing here holds up the list — the page builds its request
 * from the URL alone.
 */
export function useContactName(contactId: string | undefined): string | undefined {
  // Keyed by id rather than holding the last name resolved: while the next
  // contact's name is in flight, the previous contact's must not label its
  // chip. Seeded from the cache so a name resolved earlier renders on the
  // first paint instead of flashing the uuid.
  const [names, setNames] = useState<Record<string, string>>(() => {
    const cached = contactId ? cache.get(contactId) : undefined;
    return contactId && cached ? { [contactId]: cached } : {};
  });

  useEffect(() => {
    if (!contactId) return;
    let cancelled = false;
    fetchName(contactId).then(name => {
      if (cancelled || !name) return;
      // Returning `prev` untouched when the seed already holds this name skips
      // the re-render a cache hit would otherwise cost.
      setNames(prev => (prev[contactId] === name ? prev : { ...prev, [contactId]: name }));
    });
    return () => {
      cancelled = true;
    };
  }, [contactId]);

  return contactId ? names[contactId] : undefined;
}

/** Test-only: clear the module-scoped cache. */
export function __resetContactNameCache(): void {
  cache.clear();
  inFlight.clear();
}
